import React from 'react';
import { Box, Button, Table, Tag, Text, TextButton } from '@zillow/constellation-10';
import { IconPlusOutline } from '@zillow/constellation-icons';
import { Col, Row } from '@zg-rentals/zrs-comet';

import { useAccountContacts } from '../hooks/billingPortal';
import SpinnerContainer from './SpinnerContainer';

import RemoveZuoraContactConfirmationModal from './modals/RemoveZuoraContactConfirmationModal';
import AddContactModal from './modals/AddContactModal';
import type { AdproductsBillingPortalAccountContactDto } from '@zg-rentals/ts-adproducts-client';

export type InvoiceNotificationsManagerProps = {
  accountNumber: string;
};

const InvoiceNotificationsManager = ({ accountNumber }: InvoiceNotificationsManagerProps) => {
  const { data, isFetching, refetch } = useAccountContacts(accountNumber);
  const [showAddContactModal, setShowAddContactModal] = React.useState(false);
  const [contactToRemove, setContactToRemove] = React.useState<AdproductsBillingPortalAccountContactDto | null>(null);

  const handleSubmit = () => {
    refetch();
    setShowAddContactModal(false);
  };

  const renderTable = () => {
    if (!data) return;
    return (
      <Table.Responsive>
        <Table.Root aria-label="Invoice notifications contacts" appearance="horizontal">
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>User email</Table.HeaderCell>
              <Table.HeaderCell> </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {data.contacts.map((contact) => (
              <Table.Row key={contact.id}>
                <Table.Cell>
                  {contact.workEmail}
                  {contact.id === data.billToContactId || contact.id === data.soldToContactId ? (
                    <Tag tone="info" size="sm" css={{ marginLeft: 'xs' }}>
                      Primary
                    </Tag>
                  ) : null}
                </Table.Cell>
                <Table.Cell>
                  {contact.id !== data.billToContactId && contact.id !== data.soldToContactId ? (
                    <TextButton onClick={() => setContactToRemove(contact)}>Remove</TextButton>
                  ) : null}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      </Table.Responsive>
    );
  };

  return (
    <Box css={{ padding: 'lg' }}>
      <Box css={{ paddingBottom: 'sm' }}>
        <Row hidden={[]}>
          <Col>
            <Text textStyle="heading-sm">Invoice notifications</Text>
          </Col>
          <Col>
            <Box css={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                icon={<IconPlusOutline />}
                onClick={() => setShowAddContactModal(true)}
                tone="brand"
                emphasis="filled"
                disabled={isFetching}
              >
                Add
              </Button>
            </Box>
          </Col>
        </Row>
      </Box>
      <Row hidden={[]}>
        <Col>
          <Box css={{ paddingBottom: 'lg' }}>
            <Text>These contacts will receive invoice notification emails.</Text>
          </Box>
        </Col>
      </Row>

      {isFetching && <SpinnerContainer />}
      {data && !isFetching && renderTable()}
      {showAddContactModal && (
        <AddContactModal
          open={showAddContactModal}
          existingContacts={(data && data.contacts) || []}
          accountNumber={accountNumber}
          handleSubmit={handleSubmit}
          handleClose={() => setShowAddContactModal(false)}
        />
      )}
      {contactToRemove && (
        <RemoveZuoraContactConfirmationModal
          accountNumber={accountNumber}
          contactToRemove={contactToRemove}
          handleClose={(refresh: boolean) => {
            setContactToRemove(null);
            if (refresh) {
              refetch();
            }
          }}
        />
      )}
    </Box>
  );
};

export default InvoiceNotificationsManager;
